/**
 * 10.9IF — TELA DE CONTA NA PRIMEIRA VEZ. Thiago: "quando instalar a primeira vez deve perguntar
 * se tem conta void ou se vai logar com o google".
 *
 * Antes o jogador caía direto no jogo como convidado e só descobria que dava pra ter conta se
 * fuçasse a tela de Ranking — ou seja, a maioria jogava horas e perdia tudo ao trocar de aparelho.
 * Agora, na PRIMEIRA abertura, ele escolhe: Google, ID do Void (e-mail+senha) ou seguir como
 * convidado. A escolha não é obrigatória: "convidado" é um caminho de primeira classe, e dá pra
 * criar conta depois pelo Ranking sem perder o progresso (a conta anônima é VINCULADA).
 *
 * Só aparece uma vez: a flag fica no localStorage do aparelho (é preferência de UI, não progresso).
 */
import { GameConfig } from "../config/GameConfig";
import { i18n } from "../i18n/i18n";
import { authState, createAccount, login, signInWithProvider } from "../net/CloudAuth";

const ASKED_KEY = "voidhunter.accountAsked";

export interface AccountGateHandlers {
  /** Escolheu (entrou, criou conta ou seguiu como convidado): segue o fluxo normal do menu. */
  onDone: () => void;
}

export class AccountGate {
  private readonly root: HTMLDivElement;
  private busy = false;

  constructor(uiRoot: HTMLElement, private readonly h: AccountGateHandlers) {
    this.root = document.createElement("div");
    this.root.className = "overlay acct-gate";
    this.root.hidden = true;
    const social = GameConfig.auth.social && GameConfig.auth.google;
    this.root.innerHTML = `
      <div class="panel void-panel acct-gate-panel">
        <h2>${i18n.t("gate.title")}</h2>
        <p class="acct-gate-sub">${i18n.t("gate.sub")}</p>
        ${social ? `<button class="btn acct-social g" data-a="gate-google"><span>G</span> ${i18n.t("gate.google")}</button>` : ""}
        <button class="btn acct-gate-void" data-a="gate-void" aria-expanded="false" aria-controls="vh-gate-form">${i18n.t("gate.void")}</button>
        <div class="acct-gate-form" id="vh-gate-form" hidden>
          <input class="acct-gate-email" type="email" autocomplete="email" aria-label="${i18n.t("gate.email")}" placeholder="${i18n.t("gate.email")}" />
          <input class="acct-gate-pass" type="password" autocomplete="current-password" aria-label="${i18n.t("gate.pass")}" placeholder="${i18n.t("gate.pass")}" />
          <div class="acct-gate-btns">
            <button class="btn primary" data-a="gate-signup">${i18n.t("gate.create")}</button>
            <button class="btn" data-a="gate-login">${i18n.t("gate.enter")}</button>
          </div>
        </div>
        <div class="acct-gate-msg" role="status"></div>
        <button class="btn acct-gate-guest" data-a="gate-guest">${i18n.t("gate.guest")}</button>
        <p class="acct-gate-note">${i18n.t("gate.note")}</p>
      </div>`;
    uiRoot.appendChild(this.root);

    this.root.querySelector('[data-a="gate-google"]')?.addEventListener("click", () => void this.social());
    this.root.querySelector('[data-a="gate-void"]')!.addEventListener("click", () => {
      const f = this.root.querySelector<HTMLElement>(".acct-gate-form")!;
      f.hidden = !f.hidden;
      this.root.querySelector('[data-a="gate-void"]')!.setAttribute("aria-expanded", String(!f.hidden));
      if (!f.hidden) f.querySelector<HTMLInputElement>("input")?.focus();
    });
    this.root.querySelector('[data-a="gate-signup"]')!.addEventListener("click", () => void this.auth("signup"));
    this.root.querySelector('[data-a="gate-login"]')!.addEventListener("click", () => void this.auth("login"));
    this.root.querySelector('[data-a="gate-guest"]')!.addEventListener("click", () => this.done());
  }

  /** 10.9IF — perguntar? Só na 1ª vez do aparelho e só se ainda não há conta de verdade. */
  static async shouldAsk(): Promise<boolean> {
    try {
      if (localStorage.getItem(ASKED_KEY) === "1") return false;
    } catch {
      return false; // sem localStorage: não insiste
    }
    const st = await authState();
    return !st.signedIn || st.anonymous;
  }

  show(): void {
    this.root.hidden = false;
  }

  private done(): void {
    try {
      localStorage.setItem(ASKED_KEY, "1");
    } catch {
      /* modo privado: só não lembra */
    }
    this.root.hidden = true;
    this.h.onDone();
  }

  private msg(text: string, ok: boolean): void {
    const el = this.root.querySelector<HTMLElement>(".acct-gate-msg")!;
    el.textContent = text;
    el.style.color = ok ? "#8affc0" : "#ff9a9a";
  }

  private async social(): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.msg("…", true);
    // 10.9IL — no APK o login acontece numa aba do Chrome por cima do app: o `signInWithProvider`
    // só diz que ABRIU, e o resultado de verdade chega depois, pelo evento, quando o jogador volta.
    window.addEventListener("vh-auth", (e) => {
      const d = (e as CustomEvent<{ ok: boolean; msg: string }>).detail;
      this.busy = false;
      this.msg(d.msg, d.ok);
    }, { once: true });
    const r = await signInWithProvider("google");
    this.busy = false;
    this.msg(r.msg, r.ok);
    // se deu certo, a página sai pro Google e volta logada — a flag é marcada no retorno (Game)
    if (r.ok) {
      try {
        localStorage.setItem(ASKED_KEY, "1");
      } catch {
        /* ignora */
      }
    }
  }

  private async auth(kind: "signup" | "login"): Promise<void> {
    if (this.busy) return;
    const email = this.root.querySelector<HTMLInputElement>(".acct-gate-email")!.value;
    const pass = this.root.querySelector<HTMLInputElement>(".acct-gate-pass")!.value;
    this.busy = true;
    this.msg("…", true);
    const r = kind === "signup" ? await createAccount(email, pass) : await login(email, pass);
    this.busy = false;
    this.msg(r.msg, r.ok);
    if (!r.ok) return;
    if (kind === "login") {
      // trocou de conta: recarrega pra puxar o save dela (mesmo caminho do Ranking)
      try {
        localStorage.setItem(ASKED_KEY, "1");
      } catch {
        /* ignora */
      }
      setTimeout(() => location.reload(), 800);
      return;
    }
    setTimeout(() => this.done(), 900); // criou conta: segue pro jogo
  }
}
